<template>
  <v-dialog v-model="dialog" v-if="dialog && mailEvent" max-width="900" scrollable>
    <v-card>
      <v-card-title class="headline">
        {{ $t('mail-event.event-details') }}
        <v-spacer></v-spacer>
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>
.
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6">
            <div class="detail-section">
              <div class="detail-label">{{ $t('email') }}</div>
              <div class="detail-value email-value">
                <v-icon small color="primary" class="mr-2">mdi-email</v-icon>
                <span class="font-weight-medium flex-grow-1">{{ mailEvent.email }}</span>
                <v-btn
                  icon
                  x-small
                  @click="search(mailEvent.email)"
                  :title="$t('search-by-email')"
                  class="ml-2"
                >
                  <v-icon x-small>mdi-magnify</v-icon>
                </v-btn>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="detail-section">
              <div class="detail-label">{{ $t('mail-event.subject') }}</div>
              <div class="detail-value d-flex align-center">
                <span class="flex-grow-1">{{ mailEvent.subject || '-' }}</span>
                <v-btn
                  v-if="mailEvent.subject"
                  icon
                  x-small
                  @click="search(mailEvent.subject)"
                  :title="$t('search-by-subject')"
                  class="ml-2"
                >
                  <v-icon x-small>mdi-magnify</v-icon>
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <div class="detail-section">
              <div class="detail-label">{{ $t('mail-event.message-id') }}</div>
              <div class="detail-value">{{ mailEvent.messageId }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="detail-section">
              <div class="detail-label">{{ $t('date') }}</div>
              <div class="detail-value">{{ mailEvent.createdAt | formatDate("DD/MM/YY [à] HH[h]mm[:]ss") }}</div>
            </div>
          </v-col>
        </v-row>

        <v-expansion-panels v-if="mailEvent.payload && Object.keys(mailEvent.payload).length > 0" flat class="my-4">
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-bold">
              <v-spacer></v-spacer>
              Payload du message
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <pre class="payload-data">{{ JSON.stringify(mailEvent.payload, null, 2) }}</pre>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-divider class="my-4"></v-divider>

        <h3 class="mb-4">{{ $t('mail-event.events-timeline') }}</h3>

        <v-timeline dense class="events-timeline">
          <v-timeline-item
            v-for="(event, index) in sortedEvents"
            :key="index"
            :color="getEventColor(event.event)"
            :icon="getEventIcon(event.event)"
            small
          >
            <template v-slot:opposite>
              <div class="event-time">
                {{ event.eventTime | formatDate("DD/MM/YY [à] HH[h]mm[:]ss") }}
              </div>
            </template>

            <v-card :class="{'bounce-card': event.event === 'bounce'}">
              <v-card-title class="text-h6 pa-3">
                <v-chip
                  :color="getEventColor(event.event)"
                  :text-color="getTextColor(getEventColor(event.event))"
                  small
                  class="mr-2"
                >
                  {{ getEventLabel(event.event) }}
                </v-chip>
                <span v-if="event.event === 'bounce'" class="error--text font-weight-bold">
                  {{ $t('mail-event.bounce-warning') }}
                </span>
              </v-card-title>

              <v-card-text class="pa-3">
                <v-row dense v-if="event.ip">
                  <v-col cols="4" class="font-weight-bold">{{ $t('mail-event.ip') }}:</v-col>
                  <v-col cols="8">{{ event.ip }}</v-col>
                </v-row>

                <v-row dense v-if="event.geo">
                  <v-col cols="4" class="font-weight-bold">{{ $t('mail-event.location') }}:</v-col>
                  <v-col cols="8">{{ formatGeo(event.geo) }}</v-col>
                </v-row>

                <v-row dense v-if="event.agent">
                  <v-col cols="4" class="font-weight-bold">{{ $t('mail-event.user-agent') }}:</v-col>
                  <v-col cols="8" class="text-truncate">{{ event.agent }}</v-col>
                </v-row>

                <v-row dense v-if="event.receivedAt">
                  <v-col cols="4" class="font-weight-bold">{{ $t('mail-event.received-at') }}:</v-col>
                  <v-col cols="8">{{ event.receivedAt | formatDate }}</v-col>
                </v-row>

                <v-row dense v-if="event.event === 'click' && extractClickedUrl(event)">
                  <v-col cols="4" class="font-weight-bold">{{ $t('mail-event.clicked-url') }}:</v-col>
                  <v-col cols="8" class="d-flex align-center">
                    <a :href="extractClickedUrl(event)" target="_blank" class="text-truncate mr-2" style="max-width: 400px;">
                      {{ extractClickedUrl(event) }}
                    </a>
                    <v-btn
                      icon
                      x-small
                      @click="copyUrl(extractClickedUrl(event))"
                      :title="$t('mail-event.copy-url')"
                      class="mr-1"
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      :href="extractClickedUrl(event)"
                      target="_blank"
                      :title="$t('mail-event.open-url')"
                    >
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-expansion-panels v-if="event.additionalData && Object.keys(event.additionalData).length > 0" flat class="mt-2">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      {{ $t('mail-event.additional-data') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <pre class="additional-data">{{ JSON.stringify(event.additionalData, null, 2) }}</pre>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="dialog = false">
          {{ $t('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  data() {
    return {
      dialog: false,
      mailEvent: null,
    }
  },

  computed: {
    sortedEvents() {
      if (!this.mailEvent || !this.mailEvent.events) return []
      return [...this.mailEvent.events].sort((a, b) => {
        return new Date(a.eventTime) - new Date(b.eventTime)
      })
    }
  },

  methods: {
    open(mailEvent) {
      this.mailEvent = mailEvent
      this.dialog = true
    },

    close() {
      this.dialog = false
      setTimeout(() => {
        this.mailEvent = null
      }, 300)
    },

    getEventColor(event) {
      const colors = {
        sent: 'blue',
        open: 'teal',
        click: 'purple',
        bounce: 'red',
        blocked: 'orange',
        spam: 'deep-orange',
        unsub: 'grey',
      }
      return colors[event] || 'grey'
    },

    getEventIcon(event) {
      const icons = {
        sent: 'mdi-send',
        open: 'mdi-email-open',
        click: 'mdi-cursor-pointer',
        bounce: 'mdi-alert-circle',
        blocked: 'mdi-block-helper',
        spam: 'mdi-alert-octagon',
        unsub: 'mdi-cancel',
      }
      return icons[event] || 'mdi-information'
    },

    extractClickedUrl(event) {
      if (!event.additionalData) return null
      // Try to find URL in common fields
      return event.additionalData.url || event.additionalData.link || event.additionalData.clicked_url || null
    },

    copyUrl(url) {
      if (!url) return
      navigator.clipboard.writeText(url).then(() => {
        this.$dialog.notify.success(this.$t('copied'))
      }).catch(() => {
        this.notifyError('copy-failed')
      })
    },

    getEventLabel(event) {
      const labels = {
        sent: this.$t('mail-event.sent'),
        open: this.$t('mail-event.open'),
        click: this.$t('mail-event.click'),
        bounce: this.$t('mail-event.bounce'),
        blocked: this.$t('mail-event.blocked'),
        spam: this.$t('mail-event.spam'),
        unsub: this.$t('mail-event.unsub'),
      }
      return labels[event] || event
    },

    getTextColor(colorName) {
      // Couleurs claires qui nécessitent du texte noir
      const lightColors = ['yellow', 'lime', 'cyan', 'amber', 'light-blue', 'light-green']

      // Couleurs sombres qui nécessitent du texte blanc
      const darkColors = ['blue', 'green', 'teal', 'purple', 'red', 'orange', 'grey', 'indigo', 'deep-purple', 'deep-orange', 'brown', 'blue-grey']

      if (lightColors.includes(colorName)) {
        return 'black'
      }
      if (darkColors.includes(colorName)) {
        return 'white'
      }

      // Par défaut, texte blanc
      return 'white'
    },

    search(value) {
      this.$emit('search', value)
      this.close()
    },

    formatGeo(geo) {
      if (!geo) return '-'
      if (typeof geo === 'string') return geo
      if (typeof geo === 'object') {
        const parts = []
        if (geo.city) parts.push(geo.city)
        if (geo.region) parts.push(geo.region)
        if (geo.country) parts.push(geo.country)
        return parts.join(', ') || '-'
      }
      return '-'
    },
  }
}
</script>

<style lang="scss" scoped>
.detail-section {
  margin-bottom: 16px;

  .detail-label {
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
    margin-bottom: 4px;
    font-weight: 500;
  }

  .detail-value {
    font-size: 16px;
    color: #333;
    word-break: break-word;

    &.email-value {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      background-color: #f5f5f5;
      border-radius: 6px;
      border-left: 3px solid #1976d2;
    }
  }
}

.events-timeline {
  .event-time {
    font-size: 12px;
    color: #666;
  }

  .bounce-card {
    border-left: 4px solid #f44336;
    background-color: #ffebee;
  }

  .additional-data {
    background-color: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    font-size: 12px;
    overflow-x: auto;
    max-height: 300px;
  }
}

.payload-data {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
  max-height: 300px;
}
</style>
